import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useHistory, useParams } from "react-router-dom";
import { Button, Grid, Tooltip, Typography, useMediaQuery } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { isNil } from "lodash";
import moment from "moment";

import Table from "../../components/Layout/Table";
import { LoadingOverlay } from "../../components/Navigation/LoadingOverlay";
import { FleetContext, RiderContext } from "../../context";
import { StringUtils } from "../../util";
import uuidFromUrl from "../../util/strings/uuidFromUrl";

const { formatNumber } = StringUtils;

const useStyles = makeStyles((theme) => ({
  noPadding: {
    padding: 0,
  },
  clickableVehicle: {
    backgroundColor: theme.palette.background.overlay,
    marginRight: theme.spacing(1),
    padding: 0,
  },
}));

const GroupReservations = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const history = useHistory();
  const location = useLocation();
  const { riderId } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  const { fetchGroupReservations, groupReservations } = useContext(RiderContext);
  const { setActiveFleet, setActiveVehicle } = useContext(FleetContext);

  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const rowsPerPageOptions = [15, 30];

  useEffect(() => {
    handleFetchGroupReservations();

    return () => {
      fetchGroupReservations(null);
    };
  }, [location, riderId, rowsPerPage]);

  const handleFetchGroupReservations = async () => {
    try {
      setLoading(true);
      await fetchGroupReservations(riderId, {
        page_size: rowsPerPage,
        page: location.state?.page || 1,
      });
      setLoading(false);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return <LoadingOverlay />;
  }

  if (!groupReservations.data?.length) {
    return <Typography variant="body1">{t("Riders.Reservations.RequestError")}</Typography>;
  }

  const handlePageChange = async (page) => {
    setLoading(true);
    try {
      await fetchGroupReservations(riderId, {
        page_size: rowsPerPage,
        page: page + 1,
      });
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleNavigateToVehicle = ({ path, fleetId, vehicleId }) => {
    setActiveFleet(fleetId);
    history.push(path);
    setActiveVehicle(vehicleId);
  };

  const tableColumns = [
    {
      canonicalName: "groupReservationDate",
      name: "Date",
      renderCellContent: (row) => (row.start ? moment(row.start).format("MMM DD, YYYY") : "-"),
    },
    {
      canonicalName: "startEndTime",
      name: "Start / End",
      renderCellContent: (row) =>
        row.start
          ? `${moment(row.start).format("HH:mm")} / ${moment(row.stop).format("HH:mm")}`
          : `- ${"/"} -`,
    },
    {
      canonicalName: "shortcodes",
      name: "Vehicles",
      renderCellContent: (row) => {
        const seenCodes = new Set();
        const vehicles = row.reservations.reduce((acc, reservation) => {
          const { shortcode, guest } = reservation;
          if (!seenCodes.has(shortcode)) {
            seenCodes.add(shortcode);
            const fleetId = reservation.fleets[0];
            const vehicleId = uuidFromUrl(reservation.vehicle);
            acc.push({
              reservationId: reservation.id,
              shortcode,
              guest,
              fleetId,
              vehicleId,
              path: `/fleets/${fleetId}/vehicles/${vehicleId}`,
            });
          }
          return acc;
        }, []);

        return (
          <>
            {vehicles.map((vehicle) => (
              <Tooltip
                placement="bottom"
                title={vehicle.guest?.phone_number ?? "Host"}
                key={vehicle.reservationId}
              >
                <span>
                  <Button
                    className={classes.clickableVehicle}
                    style={{ marginBottom: isMobile ? "4px" : 0 }}
                    component={RouterLink}
                    onClick={() => handleNavigateToVehicle(vehicle)}
                    to={vehicle.path}
                  >
                    {vehicle.shortcode}
                  </Button>
                </span>
              </Tooltip>
            ))}
          </>
        );
      },
    },
    {
      canonicalName: "totalFare",
      name: "Total Fare",
      renderCellContent: (row) => {
        let amount = "--";
        let currency;
        if (!isNil(row.receipt)) {
          amount = row.receipt.total_amount * (row.receipt.exchange_rate?.rate || 1);
          currency = row.receipt.exchange_rate?.base;
        }
        return formatNumber(amount, currency);
      },
    },
    {
      canonicalName: "action",
      name: "",
      renderCellContent: (row) => (
        <RouterLink to={`/riders/${riderId}/group-reservations/${row.id}`}>
          <Button>{t("Riders.Reservations.Buttons.View")}</Button>
        </RouterLink>
      ),
    },
  ];

  return (
    <Grid item xs={12} className={classes.noPadding}>
      <Table
        muiTableProps={{ stickyHeader: true }}
        rows={groupReservations.data}
        columns={tableColumns}
        paginationProps={{
          rowsPerPage,
          rowsPerPageOptions,
          pageNumber: groupReservations.currentPage - 1,
          totalRowsCount: groupReservations.count,
          setPageNumber: handlePageChange,
          setRowsPerPage,
        }}
      />
    </Grid>
  );
};

export default GroupReservations;
